import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addMonths,
  buildDatepickerCells,
  formatFilterDateDisplay,
  formatMonthTitle,
  parseIsoDate,
  toIsoDateLocal,
} from './constants';

function FilterDateField({
  label,
  icon,
  value,
  isOpen,
  fieldRef,
  onOpen,
  onClear,
}) {
  const { t } = useTranslation(['planning', 'common']);
  const hasValue = Boolean(value);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary whitespace-nowrap">{label}</span>
      <button
        ref={fieldRef}
        type="button"
        className={`filter-date-field${isOpen ? ' is-open' : ''}${hasValue ? ' has-value' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={onOpen}
      >
        <span className="material-symbols-outlined filter-date-icon">{icon}</span>
        <span className={`filter-date-text${hasValue ? '' : ' is-placeholder'}`}>
          {hasValue ? formatFilterDateDisplay(value) : t('pickADate')}
        </span>
        <span
          className="filter-date-clear"
          role="button"
          tabIndex={-1}
          aria-label={t('clearDate', { label: label.toLowerCase() })}
          title={t('common:clear')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear?.();
          }}
        >
          <span className="material-symbols-outlined text-[14px]">close</span>
        </span>
      </button>
    </div>
  );
}

export default function FilterDatepicker({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  onOpenChange,
}) {
  const { t } = useTranslation(['planning', 'common']);
  const [activeKey, setActiveKey] = useState(null);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const startBtnRef = useRef(null);
  const endBtnRef = useRef(null);
  const pickerRef = useRef(null);

  const { weekdays, cells } = useMemo(() => buildDatepickerCells(viewDate), [viewDate]);

  const openFor = (key) => {
    const current = key === 'start' ? startDate : endDate;
    const base = current ? parseIsoDate(current) : new Date();
    setViewDate(new Date(base.getFullYear(), base.getMonth(), 1));
    setActiveKey(key);
    onOpenChange?.(true);
  };

  const close = () => {
    setActiveKey(null);
    onOpenChange?.(false);
  };

  const toggleFor = (key) => {
    if (activeKey === key) close();
    else openFor(key);
  };

  useLayoutEffect(() => {
    if (!activeKey) return;
    const btn = activeKey === 'start' ? startBtnRef.current : endBtnRef.current;
    const picker = pickerRef.current;
    if (!btn || !picker) return;

    const rect = btn.getBoundingClientRect();
    let left = rect.left;
    let top = rect.bottom + 6;
    const width = 280;
    const height = picker.offsetHeight || 320;

    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    if (left < 8) left = 8;
    if (top + height > window.innerHeight - 8) {
      top = Math.max(8, rect.top - height - 6);
    }

    setPos({ left, top });
  }, [activeKey, viewDate]);

  useEffect(() => {
    if (!activeKey) return undefined;

    const onMouseDown = (e) => {
      if (e.target.closest('.schedule-filter-datepicker')) return;
      if (e.target.closest('.filter-date-field')) return;
      close();
    };
    const onResize = () => {
      const btn = activeKey === 'start' ? startBtnRef.current : endBtnRef.current;
      const picker = pickerRef.current;
      if (!btn || !picker) return;
      const rect = btn.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + 6;
      const width = 280;
      const height = picker.offsetHeight || 320;
      if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
      if (left < 8) left = 8;
      if (top + height > window.innerHeight - 8) {
        top = Math.max(8, rect.top - height - 6);
      }
      setPos({ left, top });
    };

    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('resize', onResize);
    };
  }, [activeKey]);

  const selectDate = (iso) => {
    if (activeKey === 'start') {
      onStartChange?.(iso);
      if (endDate && iso > endDate) onEndChange?.('');
    } else if (activeKey === 'end') {
      onEndChange?.(iso);
      if (startDate && iso < startDate) onStartChange?.('');
    }
    close();
  };

  const isInRange = (iso) => {
    if (!startDate || !endDate) return false;
    return iso > startDate && iso < endDate;
  };

  return (
    <>
      <div className="filter-date-fields-row">
        <FilterDateField
          label={t('startFrom')}
          icon="calendar_month"
          value={startDate}
          isOpen={activeKey === 'start'}
          fieldRef={startBtnRef}
          onOpen={() => toggleFor('start')}
          onClear={() => onStartChange?.('')}
        />
        <FilterDateField
          label={t('endIn')}
          icon="event"
          value={endDate}
          isOpen={activeKey === 'end'}
          fieldRef={endBtnRef}
          onOpen={() => toggleFor('end')}
          onClear={() => onEndChange?.('')}
        />
      </div>

      <div
        ref={pickerRef}
        className={`schedule-filter-datepicker${activeKey ? ' is-open' : ''}`}
        style={{ left: pos.left, top: pos.top }}
        role="dialog"
        aria-label={t('chooseDate')}
        aria-hidden={!activeKey}
      >
        <div className="datepicker-header">
          <button
            type="button"
            className="datepicker-nav"
            aria-label={t('previousMonth')}
            onClick={() => setViewDate((d) => addMonths(d, -1))}
          >
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </button>
          <span className="datepicker-month-label">{formatMonthTitle(viewDate)}</span>
          <button
            type="button"
            className="datepicker-nav"
            aria-label={t('nextMonth')}
            onClick={() => setViewDate((d) => addMonths(d, 1))}
          >
            <span className="material-symbols-outlined text-[18px]">chevron_right</span>
          </button>
        </div>
        <div className="datepicker-weekdays">
          {weekdays.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="datepicker-grid">
          {cells.map((cell) => {
            const isSelected =
              (activeKey === 'start' && cell.iso === startDate) ||
              (activeKey === 'end' && cell.iso === endDate) ||
              (startDate && cell.iso === startDate) ||
              (endDate && cell.iso === endDate);

            const classNames = [
              'datepicker-day',
              cell.outside ? 'is-outside' : '',
              cell.today ? 'is-today' : '',
              isSelected ? 'is-selected' : '',
              isInRange(cell.iso) ? 'is-in-range' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={cell.iso}
                type="button"
                className={classNames}
                onClick={() => selectDate(cell.iso)}
              >
                {cell.dayNum}
              </button>
            );
          })}
        </div>
        <div className="datepicker-footer">
          <button
            type="button"
            className="datepicker-clear-btn"
            onClick={() => {
              if (activeKey === 'start') onStartChange?.('');
              else if (activeKey === 'end') onEndChange?.('');
              close();
            }}
          >
            Clear
          </button>
          <button
            type="button"
            className="datepicker-today-btn"
            onClick={() => selectDate(toIsoDateLocal(new Date()))}
          >
            Today
          </button>
        </div>
      </div>
    </>
  );
}
